import {
  GroupIdStoresInfo,
  GroupInfo,
  IAMUserState,
  IOption,
  ISelectableOption,
  LandingPageOptions,
  SetupListNavInfoByDepartment,
} from "../../../../types";
import {
  DEFAULT_OPTION,
  DEPARTMENTS,
  GL_SETUPS,
  NO_RESULTS_DEFAULT_ISelectableOption,
} from "../../constants";
import { getSelectedStoreOption } from "../storeHelpers";

export const getGroupIdOptionsFromDetailsAPIResponse = (
  groupsInfo: GroupInfo[],
  allowedGroupIds?: string[],
): ISelectableOption[] => {
  const groupOptions = groupsInfo
    .filter(
      (group: GroupInfo) =>
        group.type === "GL_GROUP" &&
        (!allowedGroupIds || allowedGroupIds.includes(group.groupId)),
    )
    .map((group) => {
      return {
        label: group.name,
        value: group.groupId,
      };
    });

  return groupOptions.length > 0
    ? groupOptions
    : [NO_RESULTS_DEFAULT_ISelectableOption()];
};

export const getStoreOptionByGroupId = ({
  selectedGroupId,
  iamUserState,
}: {
  selectedGroupId: string;
  iamUserState: IAMUserState;
}): IOption => {
  const groupIdInfo = iamUserState.eisGroups.groups.find(
    (group: GroupInfo) => group.groupId === selectedGroupId,
  );
  const stores =
    groupIdInfo?.stores?.map((store: GroupIdStoresInfo) => store.storeId) ??
    groupIdInfo?.organizationUnits;
  if (stores && stores.length > 0) {
    const storeId = [...stores].sort((s1, s2) => s1.localeCompare(s2))[0];
    return getSelectedStoreOption(storeId, iamUserState.extendedAccountInfo);
  }
  return DEFAULT_OPTION;
};

export const getSelectedGroupIdOption = (
  inputValue: string,
  groupsInfo: GroupInfo[],
): IOption => {
  if (!inputValue) return { label: "", value: "" };
  const groupInfo = groupsInfo.find(
    (groupInfo) => groupInfo.groupId === inputValue,
  );

  return groupInfo
    ? {
        label: `${groupInfo.name}`,
        value: `${groupInfo.groupId}`,
      }
    : { label: inputValue, value: inputValue };
};

export const getSelectedGroupCompanyId = (
  inputValue: string,
  groupsInfo: GroupInfo[],
): string | null => {
  if (!inputValue) return null;
  const groupInfo = groupsInfo.find(
    (groupInfo) => groupInfo.groupId === inputValue,
  );

  if (groupInfo) {
    return groupInfo.companyId ?? null;
  }

  return null;
};

export function getGroupIdOptionsBySetup({
  groupIds,
  setup,
}: {
  groupIds: GroupInfo[];
  setup: string;
}): Array<IOption> {
  const setupGroupingType = GL_SETUPS.includes(setup)
    ? "GL_GROUP"
    : "ACCOUNTING_GROUP";
  const filteredGroupIds = groupIds.filter(
    (group: GroupInfo) => group.type === setupGroupingType,
  );
  const groupIdOptions: Array<IOption> = filteredGroupIds.map(
    (group: GroupInfo) => getSelectedGroupIdOption(group.groupId, groupIds),
  );
  return groupIdOptions;
}

export function getGroupIdOptionBySetupAndStore({
  groupIds,
  setup,
  store,
}: {
  groupIds: GroupInfo[];
  setup: string;
  store: string;
}): IOption {
  const setupGroupingType = GL_SETUPS.includes(setup)
    ? "GL_GROUP"
    : "ACCOUNTING_GROUP";

  const groupIdInfo = groupIds
    .filter((group) => group.type === setupGroupingType)
    .find((group) => isGroupAssociatedWithStore(group, store));

  return getSelectedGroupIdOption(groupIdInfo?.groupId ?? "", groupIds);
}

const isGroupAssociatedWithStore = (
  group: GroupInfo,
  store: string,
): boolean => {
  if (group.type === "GL_GROUP") {
    return group.organizationUnits?.includes(store) ?? false;
  }

  return (
    group.stores?.some((storeInfo) => storeInfo.storeId === store) ?? false
  );
};

export const getAllowedGroupIds = ({
  selected,
  setupListNavInfoByDepartment,
  iamUserState,
}: {
  selected: LandingPageOptions;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  iamUserState: IAMUserState;
}): string[] => {
  const { department, groupId, departmentId } = selected;

  switch (department.value) {
    case DEPARTMENTS.ACCOUNTING: {
      const selectedGroup = iamUserState.eisGroups.groups.find(
        (groupOption) =>
          groupOption.groupId === groupId.value &&
          groupOption.type === "ACCOUNTING_GROUP",
      );

      const GLGroupsIds = iamUserState.eisGroups.groups
        .filter((groupOption) => groupOption.type === "GL_GROUP")
        .map((groupOption) => groupOption.groupId);

      // for accounting groups the organizationUnits will the the GL GroupIds
      // default to the GL groupIds (required for Intercompany setup)

      return selectedGroup?.organizationUnits ?? GLGroupsIds;
    }

    case DEPARTMENTS.SERVICE: {
      const serviceDepartmentIds =
        setupListNavInfoByDepartment[department.value]?.departmentIds ?? [];

      const storeId = serviceDepartmentIds.find(
        (departmentIdOption) =>
          departmentIdOption.departmentId === departmentId.value,
      )?.storeId;

      if (!storeId) {
        return [];
      }

      const groupIds = iamUserState.eisGroups.groups.find(
        ({ type, organizationUnits }) =>
          type === "GL_GROUP" && organizationUnits.includes(storeId),
      )?.groupId;

      return groupIds ? [groupIds] : [];
    }

    default:
      return [];
  }
};
